import React, { useEffect } from "react";
import clsx from "clsx";
import { ErrorMessage, FormikProps } from "formik";
import {
    Attributes,
    Label,
    Validation,
} from "app/modules/tp/builders/FormBuilder";
import TextInput from "./TextInput";
import { Modal } from "react-bootstrap";
import { Document, Page } from "react-pdf";
import { Actions } from "app/modules/tp/components/Modal/ModalEntity";

export type FileBase64Input<T> = {
    type: "file_base64";
    fileTypes?: string[];
    key: keyof T;
    isRequired?: boolean;
    attributes?: Attributes;
} & Attributes &
    Validation &
    Label;

export type FileBase64InputFieldProps<T> = {
    formik: FormikProps<T>;
    inputObj: FileBase64Input<T>;
    actions: Actions<T>;
    onChange?: (e: T) => void;
};

type ConversionError = {
    success: false;
    name: string;
    message: string;
    code: number;
};

type Base64 = {
    success: true;
    base64: string;
};

const decodeBase64ToFile = (
    base64: string,
    filename: string,
    filetype: string
) => {
    if (!base64) {
        return undefined;
    }
    const byteString = atob(base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: filetype });
    return new File([blob], filename, { type: filetype });
};

const convertToBase64 = (file: File) =>
    new Promise<Base64 | ConversionError>((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);

        fileReader.onload = () => {
            resolve({ success: true, base64: fileReader.result as string });
        };

        fileReader.onerror = (error) => {
            reject({ ...error, success: false });
        };
    });

const getFileLink = (file: File) => {
    const newFile = new File([file], file.name, { type: "application/pdf" });
    return URL.createObjectURL(newFile);
};

const getFileName = (file: File) => file.name;

const getFiletype = (file: File) => file.type;

export const FileBase64InputField = <T,>({
    formik,
    inputObj,
    onChange,
}: FileBase64InputFieldProps<T>) => {
    const value = (formik.values as any)[inputObj.key as string];

    const [file, setFile] = React.useState<File | undefined>(
        decodeBase64ToFile(value?.base64, value?.fileName, value?.fileType)
    );

    const [base64, setBase64] = React.useState<string>("");

    const [showModal, setShowModal] = React.useState<boolean>(false);

    const viewFile = (file?: File) => {
        if (!file) {
            return;
        }
        setShowModal(true);
    };

    const isRequiredField = (inputObj: FileBase64Input<T>) => {
        return inputObj.validation?.spec?.presence === "required";
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            return formik.setFieldError(
                inputObj.key as string,
                "File non trovato"
            );
        }
        // Max file 10MB
        if (file.size > 10000000) {
            return formik.setFieldError(
                inputObj.key as string,
                "File troppo grande (max 10MB)"
            );
        }

        if (
            inputObj.fileTypes &&
            inputObj.fileTypes.length > 0 &&
            !inputObj.fileTypes.includes(getFiletype(file))
        ) {
            return formik.setFieldError(
                inputObj.key as string,
                "Tipo di file non supportato"
            );
        }
        setFile(file);
        const base64 = await convertToBase64(file);
        if (base64?.success) {
            formik.setFieldValue(inputObj?.key as string, {
                base64: base64?.base64?.split(",")?.[1],
                fileName: getFileName(file),
                fileType: getFiletype(file)?.split("/")?.[1],
            });
            onChange && onChange(formik.values);
        }
        return formik.setFieldError(
            inputObj.key as string,
            "Errore durante la lettura del file"
        );
    };

    const inputRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!file) {
            return;
        }
        convertToBase64(file).then((base64) => {
            if (base64?.success) {
                setBase64(base64?.base64);
            }
        });
    }, [file]);

    return (
        <div className="tp-input-file">
            <Modal
                className="tp-modal"
                dialogClassName="modal-dialog modal-dialog-centered mw-900px tp-modal-dialog"
                show={showModal}
                onHide={() => setShowModal(false)}
            >
                <div className="modal-header w-100">
                    <h2 className="tp-modal-title">{/*Empty*/}</h2>
                    <div
                        className="btn btn-sm btn-icon btn-color-primary"
                        onClick={() => setShowModal(false)}
                    >
                        <i className="tp-modal-close-icon fa-solid fa-xmark"></i>
                    </div>
                </div>
                <Modal.Body>
                    <Document file={base64}>
                        <Page />
                    </Document>
                </Modal.Body>
            </Modal>

            {inputObj.label && (
                <label
                    className={`fw-bold fs-6 mb-2 ${
                        isRequiredField(inputObj) ? "required" : ""
                    } `}
                >
                    {inputObj.label}
                </label>
            )}

            <div className="tp-input-file-view">
                <TextInput
                    {...formik.getFieldProps(inputObj.key)}
                    className={"form-control form-control-solid"}
                    disabled
                    value={file ? getFileName(file) : ""}
                    {...inputObj.attributes}
                />
                <a
                    rel="noreferrer"
                    target="_blank"
                    href={file ? getFileLink(file) : "#"}
                    className="tp-btn-empty"
                    style={{ display: "block" }}
                >
                    <i className="fa-solid fa-eye icon icon-secondary"></i>
                </a>
            </div>
            <div>
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className=" btn tp-btn-primary tp-btn-bolder"
                >
                    <i className="fa-solid fa-file-arrow-up me-2"></i>
                    Carica {inputObj.label}
                </button>
            </div>
            <input
                ref={inputRef}
                hidden
                type="file"
                name={inputObj.key as string}
                onChange={handleFileChange}
                className={clsx(
                    "form-control form-control-solid mb-3 mb-lg-0",
                    {
                        "is-invalid":
                            formik?.touched[inputObj.key] &&
                            formik.errors[inputObj.key],
                    },
                    {
                        "is-valid":
                            formik?.touched[inputObj.key] &&
                            !formik.errors[inputObj.key],
                    }
                )}
                autoComplete="off"
                disabled={formik?.isSubmitting}
            />
            {formik?.touched[inputObj.key] && formik.errors[inputObj.key] && (
                <ErrorMessage
                    name={inputObj.key as string}
                    render={(error: string) => (
                        <div className="fv-plugins-message-container">
                            <div className="fv-help-block">
                                <span role="alert">{error}</span>
                            </div>
                        </div>
                    )}
                />
            )}
        </div>
    );
};
export default FileBase64InputField;
